import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  Segmented,
  Timeline,
  Descriptions,
  theme,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import {
  HistoryOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import { useAuthStore } from '../../stores/authStore';

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];
import { useWorkHistoryList, useWorkHistoryStatistics } from '../../hooks/useWorkHistory';
import { trpc } from '../../lib/api';
import type { WorkHistoryListItem } from '../../types/work-history';
import { UserDisplay, EquipmentDisplay, DateDisplay } from '../../components/common';
import { formatDateWithFallback } from '../../utils/dateFormat';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_OPTIONS: { label: string; value: FiltersState['status'] }[] = [
  { label: '完了', value: 'completed' },
  { label: '未完了', value: 'incomplete' },
  { label: 'キャンセル', value: 'cancelled' }
];

const RESULT_OPTIONS: { label: string; value: FiltersState['result'] }[] = [
  { label: '良好', value: 'good' },
  { label: '要注意', value: 'attention_needed' },
  { label: '修理必要', value: 'repair_required' }
];

const WORK_TYPE_LABELS: Record<string, string> = {
  inspection: '点検',
  maintenance: '保守',
  repair: '修理',
  replacement: '交換',
  other: 'その他'
};

const STATUS_TAG: Record<string, { color: string; label: string }> = {
  completed: { color: 'green', label: '完了' },
  incomplete: { color: 'orange', label: '未完了' },
  cancelled: { color: 'red', label: 'キャンセル' }
};

const RESULT_TAG: Record<string, { color: string; label: string }> = {
  good: { color: 'green', label: '良好' },
  attention_needed: { color: 'orange', label: '要注意' },
  repair_required: { color: 'red', label: '修理必要' }
};

type FiltersState = {
  equipmentId?: string;
  workType?: string;
  status?: 'completed' | 'incomplete' | 'cancelled';
  result?: 'good' | 'attention_needed' | 'repair_required';
  performedBy?: string;
  search?: string;
  dateRange?: [Dayjs, Dayjs];
};

type EquipmentSummary = {
  id: string;
  name?: string | null;
  displayId?: string | null;
  code?: string | null;
};

type UserSummary = {
  id: string;
  name?: string | null;
  email?: string | null;
};

const defaultFilters: FiltersState = {
  dateRange: [dayjs().subtract(30, 'day'), dayjs()]
};

type ViewMode = 'timeline' | 'list';

export default function WorkHistoryIndexPage(): JSX.Element {
  const { user } = useAuthStore();
  const { token } = theme.useToken();
  const companyId = user?.companyId;
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState<ViewMode>('timeline');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<FiltersState>(defaultFilters);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 20,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100]
  });

  const queryInput = useMemo(() => {
    return {
      companyId,
      equipmentId: filters.equipmentId,
      workType: filters.workType,
      status: filters.status,
      result: filters.result,
      performedBy: filters.performedBy,
      search: filters.search,
      startDate: filters.dateRange?.[0]?.toDate(),
      endDate: filters.dateRange?.[1]?.toDate(),
      page: pagination.current ?? 1,
      limit: pagination.pageSize ?? 20
    };
  }, [companyId, filters, pagination]);

  const {
    data: listData,
    isLoading,
    refetch
  } = useWorkHistoryList(queryInput, {
    enabled: Boolean(companyId)
  });

  const { data: statistics } = useWorkHistoryStatistics(
    {
      companyId,
      equipmentId: filters.equipmentId,
      startDate: filters.dateRange?.[0]?.toDate(),
      endDate: filters.dateRange?.[1]?.toDate()
    },
    {
      enabled: Boolean(companyId)
    }
  );

  const { data: equipments } = trpc.equipment.list.useQuery(
    {
      page: 1,
      limit: 200
    },
    { enabled: Boolean(companyId) }
  );

  const { data: users } = trpc.user.listDisplay.useQuery(
    {
      companyId,
      page: 1,
      limit: 200
    },
    { enabled: Boolean(companyId) }
  );

  const equipmentList = useMemo<EquipmentSummary[]>(() => {
    if (Array.isArray(equipments?.data)) {
      return equipments.data as EquipmentSummary[];
    }
    const items = (equipments as { items?: EquipmentSummary[] } | undefined)?.items;
    if (Array.isArray(items)) {
      return items;
    }
    return [];
  }, [equipments]);

  const userList = useMemo<UserSummary[]>(() => {
    if (!users) {
      return [];
    }
    const fromData = (users as { data?: UserSummary[] }).data;
    if (Array.isArray(fromData)) {
      return fromData;
    }
    const fromItems = (users as { items?: UserSummary[] }).items;
    if (Array.isArray(fromItems)) {
      return fromItems;
    }
    return [];
  }, [users]);

  const userSelectOptions = useMemo(
    () =>
      userList.map((userItem) => ({
        label: userItem.name ?? userItem.email ?? userItem.id,
        value: userItem.id
      })),
    [userList]
  );

  const getEquipmentLabel = (equipment: EquipmentSummary) =>
    equipment.name ?? equipment.displayId ?? equipment.code ?? equipment.id;

  const equipmentSelectOptions = useMemo(
    () =>
      equipmentList.map((equipment) => ({
        label: getEquipmentLabel(equipment),
        value: equipment.id
      })),
    [equipmentList]
  );

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination({
      ...newPagination
    });
  };

  const handleFilterChange = (changed: Partial<FiltersState>) => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    setFilters((prev) => ({
      ...prev,
      ...changed
    }));
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setPagination((prev) => ({ ...prev, current: 1, pageSize: 20 }));
  };

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const groupedByDate = useMemo(() => {
    if (!listData?.data) {
      return new Map<string, WorkHistoryListItem[]>();
    }
    const groups = new Map<string, WorkHistoryListItem[]>();
    for (const item of listData.data) {
      const dateKey = item.startTime
        ? dayjs(item.startTime).format('YYYY-MM-DD')
        : 'no-date';
      const existing = groups.get(dateKey) ?? [];
      existing.push(item);
      groups.set(dateKey, existing);
    }
    return groups;
  }, [listData?.data]);

  const columns: ColumnsType<WorkHistoryListItem> = [
    {
      title: '作業タイトル',
      dataIndex: 'workTitle',
      key: 'workTitle',
      render: (value: string, record) => {
        const scheduleId = record.id ?? record.workOrderId ?? null;
        return (
          <Space direction="vertical" size={0}>
            {scheduleId ? (
              <Button
                type="link"
                onClick={() => navigate(`/app/schedule/${scheduleId}`)}
                style={{ padding: 0, height: 'auto' }}
              >
                {value}
              </Button>
            ) : (
              <Text strong>{value}</Text>
            )}
            <Text type="secondary" style={{ fontSize: 12 }}>
              {WORK_TYPE_LABELS[record.workType ?? ''] ?? record.workType ?? '不明'}
            </Text>
          </Space>
        );
      }
    },
    {
      title: '対象設備',
      dataIndex: 'equipmentId',
      key: 'equipmentId',
      width: 200,
      render: (equipmentId: string) => (
        <EquipmentDisplay
          equipmentId={equipmentId}
          equipment={equipmentList.find((e) => e.id === equipmentId)}
          showIcon={false}
          size="small"
        />
      )
    },
    {
      title: '予定日時',
      dataIndex: 'startTime',
      key: 'startTime',
      width: 180,
      render: (value: Date) => formatDateWithFallback(value, { showTime: true })
    },
    {
      title: '実績時間 (h)',
      dataIndex: 'actualHours',
      key: 'actualHours',
      width: 120,
      render: (value: number | null) => (value ? value.toFixed(1) : '-')
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (value: string) => {
        const status = STATUS_TAG[value] ?? { color: 'default', label: value };
        return <Tag color={status.color}>{status.label}</Tag>;
      }
    },
    {
      title: '結果',
      dataIndex: 'result',
      key: 'result',
      width: 140,
      render: (value: string | null) => {
        if (!value) {
          return '-';
        }
        const result = RESULT_TAG[value] ?? { color: 'default', label: value };
        return <Tag color={result.color}>{result.label}</Tag>;
      }
    },
    {
      title: '担当者',
      dataIndex: 'performedBy',
      key: 'performedBy',
      width: 200,
      render: (value: string | undefined) =>
        value ? (
          <UserDisplay
            userId={value}
            user={userList.find((u) => u.id === value)}
            showTooltip={true}
            size="small"
          />
        ) : (
          '-'
        )
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            const scheduleId = record.id ?? record.workOrderId;
            if (scheduleId) {
              navigate(`/app/schedule/${scheduleId}`);
            } else {
              navigate(`/app/work-history/${record.displayId ?? record.id}`, {
                state: { record }
              });
            }
          }}
        >
          詳細
        </Button>
      )
    }
  ];

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <Row justify="space-between" align="middle">
        <Col>
          <Space direction="vertical" size={4}>
            <Title level={3} style={{ marginBottom: 0 }}>
              作業履歴一覧
            </Title>
            <Text type="secondary">
              作業の記録を検索・分析し、詳細を確認できます。
            </Text>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<HistoryOutlined />} onClick={() => refetch()}>
              最新の情報を取得
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12} lg={8}>
            <label>期間</label>
            <RangePicker
              value={filters.dateRange}
              onChange={(range) => handleFilterChange({ dateRange: range ?? undefined })}
              style={{ width: '100%' }}
              allowClear
            />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <label>設備</label>
            <Select
              allowClear
              placeholder="設備を選択"
              style={{ width: '100%' }}
              value={filters.equipmentId}
              options={equipmentSelectOptions}
              onChange={(value) => handleFilterChange({ equipmentId: value ?? undefined })}
              showSearch
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <label>作業カテゴリ</label>
            <Select
              allowClear
              placeholder="作業カテゴリを選択"
              style={{ width: '100%' }}
              value={filters.workType}
              options={Object.entries(WORK_TYPE_LABELS).map(([value, label]) => ({
                label,
                value
              }))}
              onChange={(value) => handleFilterChange({ workType: value ?? undefined })}
            />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <label>ステータス</label>
            <Select
              allowClear
              placeholder="ステータスを選択"
              style={{ width: '100%' }}
              value={filters.status}
              options={STATUS_OPTIONS}
              onChange={(value) => handleFilterChange({ status: value ?? undefined })}
            />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <label>判定</label>
            <Select
              allowClear
              placeholder="結果を選択"
              style={{ width: '100%' }}
              value={filters.result}
              options={RESULT_OPTIONS}
              onChange={(value) => handleFilterChange({ result: value ?? undefined })}
            />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <label>担当者</label>
            <Select
              allowClear
              placeholder="担当者を選択"
              style={{ width: '100%' }}
              value={filters.performedBy}
              options={userSelectOptions}
              onChange={(value) => handleFilterChange({ performedBy: value ?? undefined })}
              showSearch
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <label>キーワード</label>
            <Input
              allowClear
              value={filters.search}
              onChange={(event) => handleFilterChange({ search: event.target.value })}
              suffix={<SearchOutlined />}
              placeholder="作業名、設備、IDなど"
            />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Button onClick={resetFilters} style={{ marginTop: 24 }}>
              フィルターをリセット
            </Button>
          </Col>
        </Row>
      </Card>

      <Row gutter={16}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="総作業件数"
              value={statistics?.total ?? 0}
              prefix={<HistoryOutlined />}
              valueStyle={{ fontSize: 28, fontWeight: 'bold' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {statistics?.lastYearTotal !== undefined && statistics.lastYearTotal > 0
                ? `前年比: ${statistics.total - statistics.lastYearTotal >= 0 ? '+' : ''}${statistics.total - statistics.lastYearTotal}件`
                : '前年比: データなし'}
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="今月の作業"
              value={statistics?.thisMonth ?? 0}
              valueStyle={{ fontSize: 28, fontWeight: 'bold' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {statistics?.lastMonthTotal !== undefined && statistics.lastMonthTotal > 0
                ? `前月比: ${statistics.thisMonth - statistics.lastMonthTotal >= 0 ? '+' : ''}${statistics.thisMonth - statistics.lastMonthTotal}件`
                : '前月比: データなし'}
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="平均作業時間"
              value={statistics?.averageHours ?? 0}
              precision={1}
              suffix="時間"
              valueStyle={{ fontSize: 28, fontWeight: 'bold' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {statistics?.lastMonthAverageHours !== undefined && statistics.lastMonthAverageHours > 0
                ? `前月比: ${statistics.averageHours - statistics.lastMonthAverageHours >= 0 ? '+' : ''}${(statistics.averageHours - statistics.lastMonthAverageHours).toFixed(1)}h`
                : '前月比: データなし'}
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="完了率"
              value={statistics?.completionRate ?? 0}
              precision={1}
              suffix="%"
              valueStyle={{ fontSize: 28, fontWeight: 'bold' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {statistics?.lastMonthCompletionRate !== undefined && statistics.lastMonthCompletionRate > 0
                ? `前月比: ${statistics.completionRate - statistics.lastMonthCompletionRate >= 0 ? '+' : ''}${(statistics.completionRate - statistics.lastMonthCompletionRate).toFixed(1)}%`
                : '前月比: データなし'}
            </Text>
          </Card>
        </Col>
      </Row>

      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              作業履歴
            </Title>
          </Col>
          <Col>
            <Segmented
              value={viewMode}
              onChange={(value) => setViewMode(value as ViewMode)}
              options={[
                {
                  label: 'タイムライン',
                  value: 'timeline',
                  icon: <ClockCircleOutlined />
                },
                {
                  label: 'リスト',
                  value: 'list',
                  icon: <UnorderedListOutlined />
                }
              ]}
            />
          </Col>
        </Row>

        {viewMode === 'list' ? (
          <Table<WorkHistoryListItem>
            rowKey="id"
            columns={columns}
            loading={isLoading}
            dataSource={listData?.data ?? []}
            pagination={{
              ...pagination,
              total: listData?.pagination.total ?? 0,
              showTotal: (total) => `全${total}件中 ${(pagination.current ?? 1)}ページ目`
            }}
            onChange={handleTableChange}
          />
        ) : (
          <div style={{ maxHeight: '800px', overflowY: 'auto' }}>
            {Array.from(groupedByDate.entries())
              .sort(([a], [b]) => (b > a ? 1 : -1))
              .map(([dateKey, items]) => (
                <div key={dateKey} style={{ marginBottom: 24 }}>
                  <Typography.Title
                    level={5}
                    style={{
                      borderBottom: `2px solid ${token.colorBorderSecondary}`,
                      paddingBottom: 8,
                      marginBottom: 16
                    }}
                  >
                    {dateKey !== 'no-date'
                      ? `${dayjs(dateKey).format('YYYY年MM月DD日')}（${WEEKDAY_LABELS[dayjs(dateKey).day()]}）`
                      : '日付未設定'}
                  </Typography.Title>

                  <Timeline
                    items={items.map((item) => ({
                      children: (
                        <Card
                          key={item.id}
                          size="small"
                          style={{
                            marginBottom: 12,
                            borderLeft: `4px solid ${token.colorPrimary}`,
                            cursor: 'pointer',
                            transition: 'all 0.3s'
                          }}
                          hoverable
                          onClick={() => toggleExpand(item.id)}
                        >
                          <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Row gutter={16} align="middle">
                              <Col>
                                <Text strong style={{ fontSize: 14, color: token.colorTextSecondary }}>
                                  {item.startTime
                                    ? dayjs(item.startTime).format('HH:mm')
                                    : '-'}
                                </Text>
                              </Col>
                              <Col>
                                <Tag color={STATUS_TAG[item.status ?? '']?.color ?? 'default'}>
                                  {STATUS_TAG[item.status ?? '']?.label ?? item.status}
                                </Tag>
                              </Col>
                              <Col flex="auto">
                                <EquipmentDisplay
                                  equipmentId={item.equipmentId ?? undefined}
                                  equipment={equipmentList.find((e) => e.id === item.equipmentId)}
                                  showIcon={true}
                                  size="default"
                                />
                              </Col>
                              <Col>
                                <Text type="secondary">
                                  {WORK_TYPE_LABELS[item.workType ?? ''] ?? item.workType}
                                </Text>
                              </Col>
                            </Row>

                            <Row gutter={16} align="middle">
                              <Col>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  担当者:{' '}
                                </Text>
                                {item.performedBy ? (
                                  <UserDisplay
                                    userId={item.performedBy}
                                    user={userList.find((u) => u.id === item.performedBy)}
                                    showTooltip={true}
                                    size="small"
                                  />
                                ) : (
                                  <Text type="secondary" style={{ fontSize: 12 }}>-</Text>
                                )}
                              </Col>
                                <Col>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                  作業時間: {item.actualHours ? `${Number(item.actualHours).toFixed(1)}h` : '-'}
                                  </Text>
                                </Col>
                              <Col>
                                {item.result ? (
                                  <Tag color={RESULT_TAG[item.result]?.color ?? 'default'}>
                                    結果: {RESULT_TAG[item.result]?.label ?? item.result}
                                  </Tag>
                                ) : (
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    結果: -
                                  </Text>
                                )}
                              </Col>
                            </Row>

                            {expandedItems.has(item.id) && (
                              <div
                                style={{
                                  marginTop: 16,
                                  padding: 16,
                                  background: token.colorFillQuaternary,
                                  borderRadius: 8
                                }}
                              >
                                <Descriptions column={2} size="small" bordered>
                                  <Descriptions.Item label="作業ID">
                                    {item.displayId ?? item.id}
                                  </Descriptions.Item>
                                  <Descriptions.Item label="作業カテゴリ">
                                    {WORK_TYPE_LABELS[item.workType ?? ''] ?? item.workType}
                                  </Descriptions.Item>
                                  <Descriptions.Item label="作業タイトル">
                                    {item.workTitle}
                                  </Descriptions.Item>
                                  <Descriptions.Item label="担当者">
                                    {item.performedBy ? (
                                      <UserDisplay
                                        userId={item.performedBy}
                                        user={userList.find((u) => u.id === item.performedBy)}
                                        showTooltip={true}
                                        size="small"
                                      />
                                    ) : (
                                      '-'
                                    )}
                                  </Descriptions.Item>
                                  <Descriptions.Item label="作業時間">
                                    <DateDisplay value={item.startTime} showTime />
                                  </Descriptions.Item>
                                  <Descriptions.Item label="実績時間">
                                    {item.actualHours ? `${item.actualHours}時間` : '-'}
                                  </Descriptions.Item>
                                  {item.notes && (
                                    <Descriptions.Item label="備考" span={2}>
                                      {item.notes}
                                    </Descriptions.Item>
                                  )}
                                </Descriptions>
                                <div style={{ marginTop: 12, textAlign: 'right' }}>
                                  <Button
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const scheduleId = item.id ?? item.workOrderId;
                                      if (scheduleId) {
                                        navigate(`/app/schedule/${scheduleId}`);
                                      } else {
                                        navigate(`/app/work-history/${item.displayId ?? item.id}`, {
                                          state: { record: item }
                                        });
                                      }
                                    }}
                                  >
                                    詳細を見る
                                  </Button>
                                </div>
                              </div>
                            )}
                          </Space>
                        </Card>
                      )
                    }))}
                  />
                </div>
              ))}
          </div>
        )}
      </Card>
    </Space>
  );
}
